/**
 * O quadro de pendências — o que não fecha, com dono, estado e histórico. P4.
 *
 * **O que ele substitui** (`PESQUISA-ESPM.md` §23.3). A coordenação cola o
 * diagnóstico do URÂNIA num Excel e escreve, numa coluna à direita, o que foi
 * feito com cada linha: `e-mail enviado`, `aguardando a profa Carol`,
 * `cobrei o e-mail que foi enviado no dia 21.10`. As abas contam a queda —
 * `Análise 20.10 - 83` → `Análise 29.10 - 26`. *"Cada pintadinho desse é uma
 * negociação."*
 *
 * **Três decisões de tela:**
 *
 * 1. **A contagem por estado vem primeiro** — é o "83 → 26" dela, e é o que
 *    se lê para saber se a semana andou.
 * 2. **A pendência que sumiu não some da tela.** Fica no fim, marcada, até
 *    alguém confirmar. Sumir pode ser "o professor liberou a quinta" e pode
 *    ser "alguém apagou a oferta".
 * 3. **Quem poderia dar a aula está dentro da pendência** (item 30): os
 *    horários vagos da turma × quem dá aula naquele curso e série × o que
 *    cada um liberou. É o que ela monta hoje abrindo o URÂNIA professor por
 *    professor.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ErroApi,
  escola,
  horario,
  pendencias as api,
  sugestao,
  type Cenario,
  type EstadoPendencia,
  type Membro,
  type NotaDePendencia,
  type Pendencia,
  type TabelaDeElegiveis,
} from '../api'
import { Icon } from '../components/Icon'
import { Painel } from '../components/Painel'
import { rotulosDistintos } from '../rotulos'
import { Diferencas } from './Diferencas'

export const ESTADOS: { id: EstadoPendencia; rotulo: string }[] = [
  { id: 'ABERTA', rotulo: 'Aberta' },
  { id: 'EM_NEGOCIACAO', rotulo: 'Em negociação' },
  { id: 'AGUARDANDO', rotulo: 'Aguardando' },
  { id: 'RESOLVIDA', rotulo: 'Resolvida' },
  { id: 'DESCARTADA', rotulo: 'Descartada' },
]
const rotuloDo = (e: EstadoPendencia) => ESTADOS.find((x) => x.id === e)?.rotulo ?? e

const TIPO: Record<string, string> = {
  OFERTA_NAO_CABE: 'não cabe em quem dá',
  PROFESSOR_NAO_CABE: 'professor não cabe',
  PASSA_DO_TETO: 'passa do teto',
  TURMA_NAO_CABE: 'turma não cabe',
  SEM_PROFESSOR: 'sem professor',
  AULA_SEM_LUGAR: 'aula sem lugar',
  NA_CESTA: 'na cesta — posta à mão fora da disponibilidade',
  DISPONIBILIDADE_NAO_FECHA: 'professor respondeu sem cobrir a carga',
  FORMULARIO_SEM_RESPOSTA: 'professor não respondeu o formulário — subiu para a coordenação',
}

const semAcento = (s: string) =>
  s.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase()

const quando = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })

export function Pendencias({ projeto }: { projeto: string }) {
  const [cenarios, setCenarios] = useState<Cenario[]>([])
  const [cenario, setCenario] = useState('')
  const [itens, setItens] = useState<Pendencia[]>([])
  const [contagem, setContagem] = useState<Partial<Record<EstadoPendencia, number>>>({})
  const [membros, setMembros] = useState<Membro[]>([])
  const [filtro, setFiltro] = useState<EstadoPendencia | 'TODAS'>('TODAS')
  const [busca, setBusca] = useState('')
  const [aberta, setAberta] = useState<Pendencia | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)

  useEffect(() => {
    Promise.all([horario.cenarios(projeto), escola.membros(projeto).catch(() => [])])
      .then(([cs, ms]) => {
        setCenarios(cs)
        setMembros(ms)
        setCenario((c) => c || (cs[0]?.id ?? ''))
      })
      .catch((e) => setErro(e instanceof ErroApi ? e.message : 'não foi possível carregar'))
  }, [projeto])

  const recarregar = useCallback(async () => {
    if (!cenario) return
    setCarregando(true)
    try {
      const q = await api.quadro(projeto, cenario)
      setItens(q.itens)
      setContagem(q.contagem)
      setErro(null)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível ler as pendências')
    } finally {
      setCarregando(false)
    }
  }, [projeto, cenario])

  useEffect(() => {
    void recarregar()
  }, [recarregar])

  const visiveis = useMemo(() => {
    const q = semAcento(busca.trim())
    return itens.filter(
      (i) =>
        (filtro === 'TODAS' || i.estado === filtro) &&
        (!q || semAcento(`${i.mensagem} ${i.responsavel ?? ''} ${i.ultima_nota ?? ''}`).includes(q)),
    )
  }, [itens, filtro, busca])

  return (
    <div className="section">
      <header className="page-head">
        <h1>Pendências</h1>
        <p className="page-head__note">
          {itens.length} acompanhada(s) · o que não fecha, e o que foi feito com cada uma
        </p>
      </header>

      {cenarios.length > 1 && (
        <label className="field">
          <span className="field__label">Cenário</span>
          <select className="select" value={cenario} onChange={(e) => setCenario(e.target.value)}>
            {cenarios.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
        </label>
      )}

      {erro && (
        <p className="state state--pendencia" role="alert">
          <Icon name="aviso" size={14} />
          {erro}
        </p>
      )}

      <div className="segmented segmented--texto pendencias__estados" role="group" aria-label="Filtrar por estado" data-tour="estados">
        <button type="button" className="segmented__option" aria-pressed={filtro === 'TODAS'} onClick={() => setFiltro('TODAS')}>
          Todas <span className="mono">{itens.length}</span>
        </button>
        {ESTADOS.map((e) => (
          <button
            key={e.id}
            type="button"
            className="segmented__option"
            aria-pressed={filtro === e.id}
            onClick={() => setFiltro(e.id)}
          >
            {e.rotulo} <span className="mono">{contagem[e.id] ?? 0}</span>
          </button>
        ))}
      </div>

      {itens.length >= 8 && (
        <div className="filtro">
          <input
            className="input filtro__campo"
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Procurar — professor, turma, nota"
            aria-label="Procurar nas pendências"
          />
          <span className="filtro__conta">
            {visiveis.length} de {itens.length}
          </span>
        </div>
      )}

      {carregando && itens.length === 0 && <p className="field__hint">Conferindo…</p>}

      {!carregando && itens.length === 0 && (
        <div className="empty">
          <p className="empty__title">Nada pendente neste cenário.</p>
          <p className="empty__body">
            Aparece aqui o que impede a grade — professor com aula num dia que não liberou, turma
            que pede mais aula do que tem horário — e as aulas que o motor não conseguiu encaixar.
          </p>
        </div>
      )}

      {visiveis.length > 0 && (
        <div className="tabela-rolavel">
          <table className="tabela" data-tour="quadro">
            <thead>
              <tr>
                <th>Estado</th>
                <th>O que não fecha</th>
                <th>Responsável</th>
                <th>Última nota</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visiveis.map((i) => (
                <tr key={i.id} className={i.sumiu_em ? 'pendencias__sumiu' : undefined}>
                  <td>
                    <span className={`pendencias__estado pendencias__estado--${i.estado.toLowerCase()}`}>
                      {rotuloDo(i.estado)}
                    </span>
                  </td>
                  <td>
                    {i.mensagem}
                    <div className="field__hint">
                      {TIPO[i.tipo] ?? i.tipo}
                      {i.sumiu_em && ` · deixou de aparecer em ${quando(i.sumiu_em)} — confirme`}
                    </div>
                  </td>
                  <td>{i.responsavel ?? '—'}</td>
                  <td className="field__hint">
                    {i.ultima_nota ?? '—'}
                    {i.notas > 1 && ` (${i.notas} notas)`}
                  </td>
                  <td>
                    <button type="button" className="btn btn--secondary" onClick={() => setAberta(i)}>
                      Abrir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {aberta && (
        <Detalhe
          projeto={projeto}
          cenario={cenario}
          pendencia={aberta}
          membros={membros}
          aoFechar={() => {
            setAberta(null)
            void recarregar()
          }}
        />
      )}
    </div>
  )
}

const DIA = ['', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom']
const hora = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`

function Detalhe({
  projeto,
  cenario,
  pendencia,
  membros,
  aoFechar,
}: {
  projeto: string
  cenario: string
  pendencia: Pendencia
  membros: Membro[]
  aoFechar: () => void
}) {
  const [atual, setAtual] = useState(pendencia)
  const [notas, setNotas] = useState<NotaDePendencia[]>([])
  const [texto, setTexto] = useState('')
  const [elegiveis, setElegiveis] = useState<TabelaDeElegiveis | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const rotulos = useMemo(() => rotulosDistintos(elegiveis?.professores ?? []), [elegiveis])
  const [sugerida, setSugerida] = useState<{ id: string; nome: string } | null>(null)
  const [pedindo, setPedindo] = useState(false)

  async function pedirSugestao() {
    setPedindo(true)
    try {
      const r = await sugestao.pedir(projeto, cenario, atual.id)
      setSugerida({ id: r.cenario.id, nome: r.cenario.nome })
      setErro(r.faltando.length ? `A sugestão foi criada, mas não entrou na fila: ${r.faltando.join('; ')}.` : null)
      await lerNotas()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível pedir a sugestão')
    } finally {
      setPedindo(false)
    }
  }

  const lerNotas = useCallback(
    () => api.notas(projeto, atual.id).then(setNotas).catch(() => setNotas([])),
    [projeto, atual.id],
  )
  useEffect(() => {
    void lerNotas()
    if (atual.oferta_id) {
      api.elegiveis(projeto, cenario, atual.id).then(setElegiveis).catch(() => setElegiveis(null))
    }
  }, [projeto, cenario, atual.id, atual.oferta_id, lerNotas])

  async function mudar(m: { estado?: EstadoPendencia; responsavel_id?: string | null }) {
    try {
      const depois = await api.alterar(projeto, atual.id, m)
      setAtual({ ...atual, ...depois })
      setErro(null)
      await lerNotas()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível mudar')
    }
  }

  async function anotar() {
    if (!texto.trim()) return
    try {
      await api.anotar(projeto, atual.id, texto.trim())
      setTexto('')
      await lerNotas()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível anotar')
    }
  }

  return (
    <Painel titulo="Pendência" nota={TIPO[atual.tipo] ?? atual.tipo} aoFechar={aoFechar}>
      <div className="stack">
        <p>
          <strong>{atual.mensagem}</strong>
        </p>
        {atual.sumiu_em && (
          <p className="callout">
            Deixou de aparecer em {quando(atual.sumiu_em)}. Se foi resolvida, dê por resolvida; se
            sumiu por outro motivo, descarte.
          </p>
        )}

        <div className="row pendencias__campos">
          <label className="field">
            <span className="field__label">Estado</span>
            <select
              className="select"
              value={atual.estado}
              onChange={(e) => void mudar({ estado: e.target.value as EstadoPendencia })}
            >
              {ESTADOS.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.rotulo}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Responsável</span>
            <select
              className="select"
              value={atual.responsavel_id ?? ''}
              onChange={(e) => void mudar({ responsavel_id: e.target.value || null })}
            >
              <option value="">— ninguém —</option>
              {membros.map((m) => (
                <option key={m.usuario_id} value={m.usuario_id}>
                  {m.email}
                </option>
              ))}
            </select>
          </label>
        </div>

        {erro && (
          <p className="state state--pendencia" role="alert">
            <Icon name="aviso" size={14} />
            {erro}
          </p>
        )}

        <section className="stack">
          <h3 className="sub-head">O que foi feito</h3>
          {notas.length === 0 && <p className="field__hint">Nada anotado ainda.</p>}
          <ol className="pendencias__notas">
            {notas.map((n) => (
              <li key={n.id}>
                <span className="field__hint">
                  {quando(n.criado_em)} · {n.autor ?? 'sistema'}
                </span>
                <div>{n.texto}</div>
              </li>
            ))}
          </ol>
          <label className="field">
            <span className="field__label">Nova nota</span>
            <textarea
              className="input"
              rows={2}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="e-mail enviado pedindo a quinta; aguardando resposta"
            />
          </label>
          <div className="row">
            <button type="button" className="btn btn--primary" onClick={anotar} disabled={!texto.trim()}>
              Anotar
            </button>
          </div>
        </section>

        {atual.oferta_id && (
          <section className="stack" data-tour="sugestao">
            <h3 className="sub-head">Sugestão do motor</h3>
            <p className="field__hint">
              O motor procura a menor mudança na grade que faz esta aula caber — trocar uma aula de
              horário, ou de professor dentro do grupo. O resultado vira um cenário novo; este não é
              tocado.
            </p>
            {!sugerida ? (
              <div className="row">
                <button type="button" className="btn btn--primary" onClick={pedirSugestao} disabled={pedindo}>
                  {pedindo ? 'Pedindo…' : 'Pedir sugestão'}
                </button>
              </div>
            ) : (
              <>
                <p className="field__hint">
                  Cenário <strong>{sugerida.nome}</strong> — abra-o em Grade para ver a grade inteira e
                  continuar a partir dele.
                </p>
                <Diferencas projeto={projeto} cenario={sugerida.id} />
              </>
            )}
          </section>
        )}

        {elegiveis && (
          <section className="stack">
            <h3 className="sub-head">Quem poderia dar esta aula</h3>
            <p className="field__hint">
              Os horários em que a turma ainda está livre nesta grade, e quem dá aula neste curso e
              nesta série. Quem já está na oferta vem primeiro.
            </p>
            {elegiveis.motivo ? (
              <p className="field__hint">{elegiveis.motivo}</p>
            ) : elegiveis.horarios.length === 0 ? (
              <p className="field__hint">A turma não tem horário vago nesta grade.</p>
            ) : (
              <div className="tabela-rolavel">
                <table className="tabela pendencias__elegiveis">
                  <thead>
                    <tr>
                      <th>Professor</th>
                      {elegiveis.horarios.map((h) => (
                        <th key={h.slot_id} className="mono">
                          {DIA[h.dia]} {hora(h.inicio)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {elegiveis.professores.map((p) => (
                      <tr key={p.id}>
                        <th scope="row">
                          {rotulos.get(p.id) ?? p.nome}
                          {p.ja_na_oferta && <span className="field__hint"> · nesta oferta</span>}
                        </th>
                        {p.celulas.map((c, i) => (
                          <td key={i} className={`pendencias__celula pendencias__celula--${c.toLowerCase()}`}>
                            {c === 'LIVRE' ? 'pode' : c === 'NAO_LIBEROU' ? 'não liberou' : 'em aula'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>
    </Painel>
  )
}
